/**
 * Main Jarvis client wrapper
 * Provides a clean interface for interacting with Jarvis chat client
 */

import { initializeJarvis } from './initializeJarvis';
import { loadJarvisScripts } from './loadJarvis';
import type { JarvisContext, JarvisInitResponse } from './types';

const JARVIS_LAUNCHER_ID = 'jarvis-launcher';
const ADOBE_SUPPORT_URL = 'https://helpx.adobe.com/support.html';
const HIDE_CTA_STYLE_ID = 'jarvis-hide-default-cta';

type SupportLauncherMode = 'inactive' | 'adobe_help' | 'jarvis';

const JARVIS_LAUNCHER_SELECTOR = `#${JARVIS_LAUNCHER_ID}`;

function isInsideJarvisLauncher(target: EventTarget | null): boolean {
  return Boolean(
    target instanceof Element && target.closest(JARVIS_LAUNCHER_SELECTOR)
  );
}

function openInNewTabNoopener(url: string): void {
  const win = window.open(url, '_blank');
  if (win) win.opener = null;
}

const HIDE_CTA_CSS = `
  .adbmsgCta,
  [class*="adbmsgCta"],
  [id*="adbmsgCta"] {
    display: none !important;
    visibility: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }
`;

export class JarvisClient {
  private launcherClickListenerAttached = false;

  private supportLauncherCaptureAttached = false;

  private supportLauncherMode: SupportLauncherMode = 'inactive';

  private getClient() {
    return window.AdobeMessagingExperienceClient || null;
  }

  ensureSupportLauncherFallback(): void {
    if (this.supportLauncherCaptureAttached) return;
    this.supportLauncherCaptureAttached = true;
    document.addEventListener(
      'click',
      (e) => {
        if (
          !isInsideJarvisLauncher(e.target) ||
          this.supportLauncherMode !== 'adobe_help'
        ) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        openInNewTabNoopener(ADOBE_SUPPORT_URL);
      },
      { capture: true }
    );
  }

  setSupportLauncherMode(mode: SupportLauncherMode): void {
    this.supportLauncherMode = mode;
  }

  private attachLauncherClickListener(): void {
    if (this.launcherClickListenerAttached) return;
    this.launcherClickListenerAttached = true;
    document.addEventListener('click', (e) => {
      if (!isInsideJarvisLauncher(e.target)) return;
      this.openWindow({
        sourceType: 'button',
        sourceText: 'Contact Support',
      });
    });
  }

  private injectHideCTAStyle(): void {
    if (document.getElementById(HIDE_CTA_STYLE_ID)) {
      return;
    }

    const style = document.createElement('style');
    style.id = HIDE_CTA_STYLE_ID;
    style.textContent = HIDE_CTA_CSS;
    document.head.appendChild(style);
  }

  async init(
    context: JarvisContext,
    options: { env?: 'stage' | 'prod'; version?: string } = {}
  ): Promise<JarvisInitResponse | null> {
    const env = options.env || context.env || 'stage';

    if (!this.getClient()) {
      try {
        await loadJarvisScripts({
          version: options.version || 'latest',
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load Jarvis scripts:', error);
        return null;
      }
    }

    const contextWithEnv = { ...context, env };
    const result = initializeJarvis(contextWithEnv);
    if (result?.status === 'success' || result?.type === 'init_already_done') {
      this.attachLauncherClickListener();
    }
    return result;
  }

  openWindow(sourceInfo: {
    componentid?: string;
    componentver?: string;
    sourceType: 'button' | 'link' | 'img';
    sourceText?: string;
  }): void {
    const client = this.getClient();
    if (!client) {
      // eslint-disable-next-line no-console
      console.error('Jarvis client not initialized.');
      return;
    }
    client.openMessagingWindow(sourceInfo);
  }

  hideDefaultCTA(): void {
    const client = this.getClient();
    if (client) {
      try {
        client.applyExperienceOverride({ cta: 'hidden' });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Jarvis] Failed to hide CTA via API:', error);
      }
    }

    this.injectHideCTAStyle();
  }
}

// Export singleton instance
export const jarvisClient = new JarvisClient();
